'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { DeviceTypes, useUserDeviceType } from '@/hooks/useUserDeviceType';
import { getCookie } from '@/utils/cookies';
import styles from './sierrachatbutton.module.scss';

const SIERRA_AGENT_TOKEN = 'nulaz9wBijgxP-EBkS6Nez8dnG3Z2ycudo9sHsEgwGA'; // this is not a secret
const SIERRA_SCRIPT_URL = `https://sierra.chat/agent/${SIERRA_AGENT_TOKEN}/sxm`;
const SIERRA_SESSION_COOKIE = 'sierra-chat-session';
const SHOW_NATIVE_SIERRA_CHAT = 'showNativeSierraChat' as const;
const BANNER_SHOW_DELAY_MS = 10000; // 10 second delay for proactive banner where enabled

// Reads the sierra-chat-session cookie to determine if there's an active and open chat session
const hasActiveOpenSierraSession = () => {
    try {
        const session = JSON.parse(getCookie(SIERRA_SESSION_COOKIE) ?? '{}');

        return session?.['chat-open'] === 'yes';
    } catch {
        return false;
    }
};

const isProactiveBannerEnabled = (): boolean => !!(window as BrowserWindow).sierraSXMConfig?.proactiveBannerEnabled;

// By default this button is HIDDEN
// Adobe Launch currently controls the pages it is shown, and triggers this component by setting window.SXM.showNativeSierraChat = true
export default function SierraChatButton() {
    const scriptInjected = useRef(false);
    const deviceType = useUserDeviceType();
    const [isMounted, setIsMounted] = useState(false);
    const [showProactiveBanner, setShowProactiveBanner] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // Show the proactive banner on whitelisted pages (see sierraSXMConfig.proactiveBannerEnabled) and desktop devices
    useEffect(() => {
        if (deviceType !== DeviceTypes.desktop || !isMounted || !isProactiveBannerEnabled()) return;
        const id = window.setTimeout(() => setShowProactiveBanner(true), BANNER_SHOW_DELAY_MS);
        return () => window.clearTimeout(id);
    }, [isMounted, deviceType]);

    // Dismiss the banner immediately
    const dismissBanner = useCallback(() => setShowProactiveBanner(false), []);

    // Set state based on open/close events
    useEffect(() => {
        ((window as BrowserWindow).sierraSXMConfig ??= {}).onClose = () => setIsOpen(false);
        ((window as BrowserWindow).sierraSXMConfig ??= {}).onOpen = () => {
            setIsOpen(true);
            setShowProactiveBanner(false);
        };
    }, []);

    // Intercept assignments to window.SXM.showNativeSierraChat so the component reacts when it changes
    useEffect(() => {
        (window as BrowserWindow).SXM ??= {} as BrowserWindow['SXM'];
        let value = (window as BrowserWindow).SXM[SHOW_NATIVE_SIERRA_CHAT];
        setIsMounted(value === true);

        Object.defineProperty((window as BrowserWindow).SXM, SHOW_NATIVE_SIERRA_CHAT, {
            get: () => value,
            set: (newValue: boolean | null) => {
                value = newValue;
                setIsMounted(newValue === true);
            },
            configurable: true,
        });
        return () => {
            Object.defineProperty((window as BrowserWindow).SXM, SHOW_NATIVE_SIERRA_CHAT, {
                value,
                configurable: true,
                writable: true,
            });
        };
    }, []);

    const updateSubscriberContext = useCallback(() => {
        const w = window as BrowserWindow;

        const subscriberContext = w?.sierraSXMConfig?.subscriberContext;
        if (subscriberContext) {
            w?.sierraSXM?.updateSubscriberContext(subscriberContext);
            return;
        }
    }, []);

    // Inject the script tag to download the SierraChat script
    // Resolves a promise when loaded to signal ready
    const injectScript = useCallback((): Promise<void> => {
        if (scriptInjected.current) return Promise.resolve(); // Ensures we only load the script once
        scriptInjected.current = true;

        const script = document.createElement('script');
        script.type = 'module';
        script.src = SIERRA_SCRIPT_URL;

        return new Promise((resolve) => {
            script.onload = () => {
                updateSubscriberContext();
                resolve();
            };
            document.head.appendChild(script);
        });
    }, [updateSubscriberContext]);

    // Injects script (if necessary) and opens Sierra Chat window with correct subscriber context
    const launchChat = useCallback(() => {
        return injectScript().then(() => {
            updateSubscriberContext();
            (window as BrowserWindow)?.sierraSXM?.openChatModal();
        });
    }, [injectScript, updateSubscriberContext]);

    // Restore an open chat after navigation/refresh
    useEffect(() => {
        if (!hasActiveOpenSierraSession() || !isMounted) return;
        injectScript();
    }, [launchChat, isMounted, injectScript]);

    // Hack - Suppress Sierra's own launcher button as we have our own; otherwise the script automatically loads one in too!
    useEffect(() => {
        if (!isMounted) {
            return;
        }
        const style = document.createElement('style');
        style.textContent = '[data-sierra-chat-launcher] { display: none !important; }';
        document.head.appendChild(style);
        return () => style.remove();
    }, [isMounted]);

    // Proxy sierraSXM so any method call before Sierra loads triggers our lazy-load flow, then forwards to the real object once it's available.
    // The proxy reference is kept to detect if Sierra hasn't replaced it yet and bail rather than recurse.
    useEffect(() => {
        // This isn't as scary as it looks. We're just ensuring any request
        //  to window.sierraSXM will never get dropped because it hasn't
        //  already been initialised
        const proxy = new Proxy({} as BrowserWindow['sierraSXM'], {
            get:
                (_, prop) =>
                async (...args: unknown[]) => {
                    await injectScript();
                    const real = (window as BrowserWindow).sierraSXM;
                    if (real === proxy) return;
                    (real as Record<PropertyKey, (...a: unknown[]) => void>)[prop]?.(...args);
                },
        });
        (window as BrowserWindow).sierraSXM = proxy;
    }, [injectScript]);

    return isMounted
        ? createPortal(
              <>
                  {showProactiveBanner && (
                      <div className={styles.proactiveBannerWrapper} part="banner" aria-expanded={isOpen}>
                          <button
                              type="button"
                              className={styles.proactiveBannerButton}
                              onClick={launchChat}
                              aria-label="Open chat"
                          >
                              <img
                                  className={styles.proactiveBanner}
                                  src="https://www.siriusxm.com/content/dam/sxm-com/logos/chat/ProActiveChat-Smallest-LightBlue.png"
                                  alt=""
                                  width={368}
                                  height={192}
                              />
                          </button>
                          <button
                              type="button"
                              className={styles.dismissButton}
                              onClick={dismissBanner}
                              aria-label="Dismiss chat banner"
                          >
                              <img
                                  className={styles.dismissIcon}
                                  src="https://www.siriusxm.com/content/dam/sxm-com/logos/chat/Close%20and%20Remove.svg"
                                  alt=""
                                  width={24}
                                  height={24}
                              />
                          </button>
                      </div>
                  )}

                  <button
                      type="button"
                      className={styles.button}
                      onClick={launchChat}
                      aria-label="Open chat"
                      aria-expanded={isOpen}
                  >
                      <ImageWrapper
                          src="/resources/images/chatbubble.png"
                          alt=""
                          optimizedWidth={100}
                          optimizedHeight={100}
                          fetchPriority="auto"
                          loadingStrategy="eager"
                      />
                  </button>
              </>,
              document.body,
          )
        : null;
}
